import type { Attachment } from "@ark-bot/protocol";

import type { Chip } from "./mentions.js";

/**
 * What is typed in the message box and not sent yet, one per chat, as a
 * messenger keeps it: going to another bot leaves the words where they were,
 * and coming back finds them there. Kept on this device (a reload keeps
 * them), not on the host: a half-written message is the writer's own.
 */
export interface Draft {
  readonly text: string;
  readonly chips: readonly Chip[];
  readonly attachments: readonly Attachment[];
}

export type Drafts = Readonly<Record<string, Draft>>;

export const DRAFTS_KEY = "ark-bot.drafts";

const EMPTY: Draft = { text: "", chips: [], attachments: [] };

/** Whether there is anything to keep: words (spaces alone are not) or a picture. */
export function isEmptyDraft(draft: Draft): boolean {
  return draft.text.trim().length === 0 && draft.attachments.length === 0;
}

/** The drafts as stored; anything unreadable is no drafts rather than an error. */
export function parseDrafts(raw: string | null): Drafts {
  if (raw == null) return {};
  try {
    const value: unknown = JSON.parse(raw);
    if (value == null || typeof value !== "object" || Array.isArray(value)) return {};
    const drafts: Record<string, Draft> = {};
    for (const [id, one] of Object.entries(value as Record<string, unknown>)) {
      if (one == null || typeof one !== "object") continue;
      const { text, chips, attachments } = one as Partial<Draft>;
      if (typeof text !== "string") continue;
      drafts[id] = { text, chips: Array.isArray(chips) ? chips : [], attachments: Array.isArray(attachments) ? attachments : [] };
    }
    return drafts;
  } catch {
    return {};
  }
}

/** The draft for one chat; an empty one where nothing was left. */
export function draftFor(drafts: Drafts, id: string): Draft {
  return drafts[id] ?? EMPTY;
}

/** The drafts with this chat's set to `draft`; an empty draft is dropped rather than kept. */
export function withDraft(drafts: Drafts, id: string, draft: Draft): Drafts {
  const { [id]: _was, ...rest } = drafts;
  return isEmptyDraft(draft) ? rest : { ...rest, [id]: draft };
}

function stored(): Drafts {
  try {
    return parseDrafts(localStorage.getItem(DRAFTS_KEY));
  } catch {
    return {};
  }
}

/** This device's draft for a chat. */
export function loadDraft(id: string): Draft {
  return draftFor(stored(), id);
}

/** Keeps (or, emptied, forgets) this device's draft for a chat; a private window that refuses storage keeps nothing. */
export function saveDraft(id: string, draft: Draft): void {
  try {
    const next = withDraft(stored(), id, draft);
    if (Object.keys(next).length === 0) localStorage.removeItem(DRAFTS_KEY);
    else localStorage.setItem(DRAFTS_KEY, JSON.stringify(next));
  } catch {
    // Nowhere to keep it: the draft lasts as long as the box is open.
  }
}
